import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, num, fmtDate } from "@/lib/format";
import { Card, Loading, PageHeader, Button, Badge, EmptyState } from "@/components/common";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const ST = { open: { label: "Aktif", c: "bg-sky-100 text-sky-700" }, partial: { label: "Sebagian", c: "bg-amber-100 text-amber-700" }, closed: { label: "Selesai", c: "bg-emerald-100 text-emerald-700" } };

export default function ConsignmentReport() {
  const { branches, isManagement } = useAuth();
  const nav = useNavigate();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [branchId, setBranchId] = useState("");
  const [partner, setPartner] = useState("");
  const [preset, setPreset] = useState("all");
  const [data, setData] = useState(null);

  const params = useMemo(() => ({ date_from: from || undefined, date_to: to || undefined, branch_id: branchId || undefined, partner: partner.trim() || undefined }), [from, to, branchId, partner]);
  const load = () => { setData(null); api.get("/consignments/report/summary", params).then(setData).catch(() => setData(null)); };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [params]);

  const applyPreset = (key) => {
    setPreset(key); const now = new Date();
    if (key === "all") { setFrom(""); setTo(""); } else if (key === "today") { setFrom(iso(now)); setTo(iso(now)); }
    else if (key === "7d") { const d = new Date(); d.setDate(d.getDate() - 6); setFrom(iso(d)); setTo(iso(now)); }
    else if (key === "month") { setFrom(iso(new Date(now.getFullYear(), now.getMonth(), 1))); setTo(iso(now)); }
  };

  const sources = (branches || []).filter((b) => b.type === "hub" || b.type === "store");
  const sm = data?.summary;

  return (
    <>
      <PageHeader title="Laporan Titip Pasar" subtitle="Konsinyasi · terpisah dari Laporan Penjualan POS" icon={Store}
        actions={<Button variant="outline" onClick={load} data-testid="creport-refresh"><RefreshCw size={16} /> Refresh</Button>} />

      <div className="flex flex-wrap gap-2 mb-3">
        {[["all", "Semua"], ["today", "Hari ini"], ["7d", "7 hari"], ["month", "Bulan ini"]].map(([k, l]) => <button key={k} onClick={() => applyPreset(k)} data-testid={`creport-preset-${k}`} className={`px-3 h-9 rounded-full text-sm font-medium ${preset === k ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>)}
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mb-3 max-w-2xl">
        <input value={from} onChange={(e) => { setFrom(e.target.value); setPreset(""); }} placeholder="Dari (2026-06-01)" data-testid="creport-from" className="h-10 px-3 rounded-xl border border-slate-300" />
        <input value={to} onChange={(e) => { setTo(e.target.value); setPreset(""); }} placeholder="Sampai (2026-06-30)" data-testid="creport-to" className="h-10 px-3 rounded-xl border border-slate-300" />
      </div>
      {isManagement && sources.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-3">
          <button onClick={() => setBranchId("")} className={`px-3 h-8 rounded-full text-xs font-medium ${!branchId ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>Semua toko asal</button>
          {sources.map((b) => <button key={b.id} onClick={() => setBranchId(b.id)} data-testid={`creport-branch-${b.id}`} className={`px-3 h-8 rounded-full text-xs font-medium ${branchId === b.id ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{b.name}</button>)}
        </div>
      )}
      <input value={partner} onChange={(e) => setPartner(e.target.value)} placeholder="Filter nama toko / mitra yang dititip…" data-testid="creport-partner" className="w-full max-w-md h-10 px-3 rounded-xl border border-slate-300 mb-4" />

      {!data ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-3" data-testid="creport-metrics">
            <Metric label="1. Total Produk Dititip" value={`${num(sm.qty_sent)} unit`} sub={`${num(sm.bundles_sent)} bundle · ${sm.notes} nota`} c="border-l-primary" testid="creport-m-qty" />
            <Metric label="2. Total Nominal Dititip" value={rupiah(sm.value_sent)} sub="nilai titip (qty × harga titip)" c="border-l-sky-500" testid="creport-m-value" />
            <Metric label="3. Total Retur Bagus" value={`${num(sm.qty_returned)} unit`} sub={`kembali ke stok · ${rupiah(sm.return_value)}`} c="border-l-emerald-500" testid="creport-m-return" />
            <Metric label="4. Barang Rusak / Spoilage" value={`${num(sm.qty_damaged)} unit`} sub={`rugi ${rupiah(sm.damaged_loss)}`} c="border-l-rose-500" testid="creport-m-damaged" />
            <Metric label="5. Pendapatan Konsinyasi" value={rupiah(sm.revenue)} sub={`${num(sm.qty_sold)} unit laku · bersih ${rupiah(sm.net_result)}`} c="border-l-emerald-500" testid="creport-m-revenue" />
            <Metric label="Sisa Masih Dititip" value={`${num(sm.qty_remaining)} unit`} sub="belum laku / retur / rusak" c="border-l-amber-500" testid="creport-m-remaining" />
          </div>
          <Card className="p-4 mb-4 bg-primary/5 border-primary/20">
            <h3 className="text-[11px] font-bold tracking-wide text-primary mb-1">HASIL BERSIH UNIT TITIP PASAR</h3>
            <KV k="Pendapatan konsinyasi (kotor)" v={rupiah(sm.revenue)} />
            <KV k="HPP barang laku" v={`- ${rupiah(sm.cost_sold)}`} />
            <KV k="Beban spoilage / rusak" v={`- ${rupiah(sm.damaged_loss)}`} />
            <KV k="Hasil bersih" v={rupiah(sm.net_result)} bold />
          </Card>

          <h3 className="font-semibold text-slate-700 mb-2">Per Toko / Mitra Titip ({data.by_partner.length})</h3>
          {data.by_partner.length === 0 ? <EmptyState icon={Store} title="Belum ada data" message="Belum ada data pada periode ini." /> : (
            <div className="space-y-2 mb-4">{data.by_partner.map((p) => (
              <Card key={p.partner_name} className="p-4" data-testid={`creport-partner-${p.partner_name}`}>
                <div className="flex justify-between"><span className="font-semibold text-slate-800">{p.partner_name}</span><span className="font-bold text-emerald-600">{rupiah(p.revenue)}</span></div>
                <div className="text-xs text-slate-400 mt-0.5">Unit {p.unit_name} · {p.notes} nota · nilai titip {rupiah(p.value_sent)}</div>
                <StatRow r={p} />
              </Card>
            ))}</div>
          )}

          <h3 className="font-semibold text-slate-700 mb-2">Per Barang ({data.by_item.length})</h3>
          <div className="space-y-2 mb-4">{data.by_item.map((it) => (
            <Card key={it.item_id} className="p-4" data-testid={`creport-item-${it.item_id}`}>
              <div className="flex justify-between"><span className="font-semibold text-slate-800">{it.item_name}</span><span className="font-bold text-emerald-600">{rupiah(it.revenue)}</span></div>
              <StatRow r={it} />
            </Card>
          ))}</div>

          <h3 className="font-semibold text-slate-700 mb-2">Daftar Nota ({data.notes.length})</h3>
          <div className="space-y-2">{data.notes.map((n) => { const st = ST[n.status] || { label: n.status, c: "bg-slate-100 text-slate-500" }; return (
            <Card key={n.id} className="p-4 cursor-pointer" onClick={() => nav(`/consignment`)} data-testid={`creport-nota-${n.consignment_no}`}>
              <div className="flex justify-between items-center"><span className="text-xs text-slate-500 font-medium">{n.consignment_no} · {fmtDate(n.sent_at)}</span><Badge className={st.c}>{st.label}</Badge></div>
              <div className="font-semibold text-slate-800">{n.partner_name}</div>
              <div className="text-xs text-slate-400">{n.source_name} → {n.unit_name} · dititip {num(n.qty_sent)} unit ({rupiah(n.value_sent)}) · retur {num(n.qty_returned)} · rusak {num(n.qty_damaged)} · sisa {num(n.qty_remaining)}</div>
              <div className="text-sm font-bold text-emerald-600 mt-1">Pendapatan {rupiah(n.revenue)}</div>
            </Card>
          ); })}</div>
        </>
      )}
    </>
  );
}

const Metric = ({ label, value, sub, c, testid }) => (<Card className={`p-4 border-l-4 ${c}`} data-testid={testid}><div className="text-xs text-slate-500 font-medium min-h-[32px]">{label}</div><div className="text-lg font-bold text-slate-800 mt-1">{value}</div>{sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}</Card>);
const KV = ({ k, v, bold }) => (<div className={`flex justify-between py-0.5 text-sm ${bold ? "font-bold text-slate-800" : "text-slate-600"}`}><span>{k}</span><span>{v}</span></div>);
const StatRow = ({ r }) => (
  <div className="grid grid-cols-5 gap-2 mt-3 text-center">
    {[["Dititip", num(r.qty_sent), "text-slate-700"], ["Laku", num(r.qty_sold), "text-emerald-600"], ["Retur", num(r.qty_returned), "text-sky-600"], ["Rusak", num(r.qty_damaged), "text-rose-600"], ["Sisa", num(r.qty_remaining), "text-amber-600"]].map(([l, v, c]) => (
      <div key={l}><div className="text-[9px] text-slate-400">{l}</div><div className={`text-sm font-semibold ${c}`}>{v}</div></div>
    ))}
  </div>
);
